'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import Image from 'next/image'
import { CareAndShareContainer } from './container'

import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useCareAndShareConfigs } from '@/hooks/react-query/care-and-share/use-configs'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { MoneySvg } from '../svg/money'
import { PersonSvg } from '../svg/person'
import { ProductSvg } from '../svg/product'
import { ProjectSvg } from '../svg/project'
import { CareShareJourney } from './care-share-journey'

export const CampaignHighlightSection = () => {
  const t = useTranslations()
  const { data: careAndShareData, isLoading } = useCareAndShareConfigs()

  const list = [
    {
      title: t('care_and_share_hardcode.highlight.stats.customers.title'),
      description: t(
        'care_and_share_hardcode.highlight.stats.customers.description'
      ),
      svg: <PersonSvg />,
    },
    {
      title: t('care_and_share_hardcode.highlight.stats.amount.title'),
      description: t(
        'care_and_share_hardcode.highlight.stats.amount.description'
      ),
      svg: <MoneySvg />,
    },
    {
      title: t('care_and_share_hardcode.highlight.stats.products.title'),
      description: t(
        'care_and_share_hardcode.highlight.stats.products.description'
      ),
      svg: <ProductSvg />,
    },
    {
      title: t('care_and_share_hardcode.highlight.stats.projects.title'),
      description: t(
        'care_and_share_hardcode.highlight.stats.projects.description'
      ),
      svg: <ProjectSvg />,
    },
  ]

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  const [highlightApi, setHighlightApi] = useState<CarouselApi>()
  const [highlightCurrent, setHighlightCurrent] = useState(0)
  const [highlightCount, setHighlightCount] = useState(0)

  const doThings = careAndShareData?.do_things

  useEffect(() => {
    if (!api) {
      return
    }

    setCurrent(api.selectedScrollSnap())

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap())
    }

    api.on('select', onSelect)

    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  useEffect(() => {
    if (!highlightApi) {
      return
    }

    setHighlightCount(highlightApi.scrollSnapList().length)
    setHighlightCurrent(highlightApi.selectedScrollSnap())

    const onSelect = () => {
      setHighlightCurrent(highlightApi.selectedScrollSnap())
    }

    highlightApi.on('select', onSelect)

    return () => {
      highlightApi.off('select', onSelect)
    }
  }, [highlightApi])

  if (isLoading) {
    return <CampaignHighlightSectionSkeleton />
  }

  if (!doThings || doThings.length === 0) {
    return null
  }

  const currentContent = doThings[current]

  return (
    <>
      <CareAndShareContainer className='pb-0 pt-5 lg:pb-4'>
        <div className='grid grid-cols-1 lg:grid-cols-2 lg:gap-x-10'>
          <div className=''>
            <h2 className='font-criteria text-4xl font-semibold italic !leading-tight text-neutral-900 lg:text-6xl'>
              {t.rich('care_and_share_hardcode.highlight.quote_title', {
                span: (chunks) => (
                  <span className='text-primary'>{chunks}</span>
                ),
              })}
            </h2>
            <p className='mt-5 text-base leading-6 text-neutral-700 md:text-lg'>
              {t.rich('care_and_share_hardcode.highlight.quote_description')}
            </p>

            <ul className='mt-8 grid grid-cols-2 gap-x-4 gap-y-6 font-sans lg:mt-10 lg:gap-x-8 lg:gap-y-8'>
              {list.map((item, index) => (
                <li key={index} className=''>
                  <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#273BCD33] p-3 lg:h-16 lg:w-16'>
                    {item.svg}
                  </div>
                  <p className='mb-2 mt-3 text-lg font-semibold leading-6 text-primary lg:text-2xl'>
                    {item.title}
                  </p>
                  <p className='text-sm leading-5 text-neutral-800 lg:text-base'>
                    {item.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div className='relative mt-8'>
            <Carousel
              setApi={setHighlightApi}
              opts={{
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                  stopOnInteraction: false,
                }),
              ]}
              className='w-full'
            >
              <CarouselContent>
                {highlightImages.map((image, index) => (
                  <CarouselItem key={index}>
                    <Image
                      src={ImageHelper.getImageUrl(image)}
                      alt={`care and share image ${index + 1}`}
                      width={654}
                      height={600}
                      className='aspect-[654/600] w-full rounded-2xl object-cover'
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className='absolute bottom-4 left-1/2 flex -translate-x-1/2 transform gap-x-2'>
                {Array.from({ length: highlightCount }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => highlightApi?.scrollTo(index)}
                    className={`h-2 w-2 rounded-full border border-white ${highlightCurrent === index ? 'bg-white' : 'bg-transparent'}`}
                  />
                ))}
              </div>
            </Carousel>
          </div>
        </div>

        <div className='mt-10 grid max-md:gap-y-4 md:mt-20 lg:grid-cols-3 lg:gap-x-8 2xl:mt-13'>
          <div className='mt-4 flex flex-col justify-between rounded-2xl bg-[linear-gradient(119.73deg,#132294_0%,#273BCD_57.71%)] p-6 text-white md:p-8 lg:mt-0 2xl:p-14'>
            {currentContent && (
              <div className='flex flex-col justify-between gap-y-6'>
                <div
                  className='font-criteria text-3xl font-semibold !leading-tight md:text-3xl 2xl:text-[44px]'
                  dangerouslySetInnerHTML={{ __html: currentContent?.title }}
                ></div>
                <div>
                  {currentContent?.sub_title ? (
                    <div
                      className='font-criteria text-xl font-semibold !leading-tight md:text-2xl 2xl:text-3xl'
                      dangerouslySetInnerHTML={{
                        __html: currentContent?.sub_title,
                      }}
                    />
                  ) : null}

                  {currentContent?.logo ? (
                    <div className='flex items-center gap-x-4 2xl:mt-10'>
                      <Image
                        src={ImageHelper.getImageUrl(currentContent.logo)}
                        alt={currentContent.title}
                        width={112}
                        height={51}
                        className='w-auto object-contain'
                      />
                    </div>
                  ) : null}
                  <p
                    className='mt-4 text-base md:mt-6 md:text-lg 2xl:text-xl'
                    dangerouslySetInnerHTML={{
                      __html: currentContent.description,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              duration: 50,
              slidesToScroll: 1, // Scroll one content group at a time
              loop: true,
            }}
            className='w-full lg:col-span-2'
          >
            <CarouselContent className='ml-0'>
              {doThings.map((item, index) => (
                <CarouselItem
                  className='pl-2 first-of-type:pl-0 last-of-type:pr-2'
                  key={index}
                >
                  <div className='grid grid-cols-2 gap-x-2'>
                    <Image
                      src={ImageHelper.getImageUrl(item.image_1)}
                      alt={item.title}
                      width={613}
                      height={290}
                      className='h-40 w-full rounded-lg object-cover md:h-full md:rounded-3xl'
                    />
                    <Image
                      src={ImageHelper.getImageUrl(item.image_2)}
                      alt={item.title}
                      width={613}
                      height={290}
                      className='h-40 w-full rounded-lg object-cover md:h-full md:rounded-3xl'
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselNavigation />
          </Carousel>
        </div>

        <div className='mt-10 flex w-full justify-center'>
          <Link href='/hanh-trinh-care-and-share'>
            <Button size='large' variant='outline'>
              {t('care_and_share_hardcode.journey.button')}
            </Button>
          </Link>
        </div>
      </CareAndShareContainer>

      <CareShareJourney />
    </>
  )
}

const CampaignHighlightSectionSkeleton = () => {
  return (
    <CareAndShareContainer className='pt-5'>
      <div className='grid grid-cols-1 lg:grid-cols-2 lg:gap-x-10'>
        <div className=''>
          <div className='h-16 w-3/4 animate-pulse rounded-lg bg-gray-200' />
          <div className='mt-5 h-8 w-full animate-pulse rounded-lg bg-gray-200' />
          <ul className='mt-8 grid grid-cols-2 gap-x-4 gap-y-6 lg:mt-10 lg:gap-x-8 lg:gap-y-8'>
            {Array.from({ length: 4 }).map((_, index) => (
              <li key={index} className=''>
                <div className='h-12 w-12 animate-pulse rounded-2xl bg-gray-200 lg:h-16 lg:w-16' />
                <div className='mb-2 mt-3 h-6 w-1/2 animate-pulse rounded-lg bg-gray-200' />
                <div className='h-5 w-3/4 animate-pulse rounded-lg bg-gray-200' />
              </li>
            ))}
          </ul>
        </div>
        <div className='mt-8 h-[600px] w-full animate-pulse rounded-2xl bg-gray-200' />
      </div>
      <div className='mt-10 grid max-md:gap-y-4 md:mt-20 lg:grid-cols-3 lg:gap-x-8 2xl:mt-13'>
        <div className='h-80 w-full animate-pulse rounded-2xl bg-gray-200 lg:h-auto' />
        <div className='h-80 w-full animate-pulse rounded-2xl bg-gray-200 lg:col-span-2 lg:h-auto' />
      </div>
    </CareAndShareContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-3 top-1/2 h-7 w-7 -translate-y-1/2'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-3 top-1/2 h-7 w-7 -translate-y-1/2'
        variant='black'
        hideIfEnd
      />
    </>
  )
}

const highlightImages = [
  '/image/2026/02/04/Frame_87765.jpg',
  '/image/2026/02/04/Frame_87832.jpg',
  '/image/2026/02/04/Frame_87833.jpg',
]
